import type { CaptureWorkLogState } from '../../data/queries/use-capture-work-log';
import { sessionOwnerUserId, useSession } from '../auth/session';
import { buildWorkLogLines } from './work-log';
import './captures.css';

/**
 * HISTORY on Capture Detail — M6 / S06-01. Read only.
 *
 * The Work Log has been written since M3 and read by nobody. This shows it:
 * newest first, one line per event, **what happened · who · when**.
 *
 * **NOTHING HERE MUTATES ANYTHING.** No edit, no undo, no delete, no filter,
 * no pagination. The Work Log is append-only in the database — no application
 * role holds INSERT, UPDATE or DELETE on it — and this screen is the reading
 * end of that. If a control ever appears here, something has gone wrong.
 *
 * **THE QUERY IS OWNED BY THE SCREEN, not by this component** (M6 / S06-02).
 * Capture Detail now has a mutation of its own — the status control — and it
 * must refresh BOTH the Capture and this History after a successful change.
 * Holding the state one level up is what lets one `reload()` do that, with no
 * global store and no cross-feature plumbing.
 *
 * **An M5 photo action still does NOT refresh this section.** That boundary is
 * unchanged: reopening the Capture shows the new photo event. Changing proven
 * photo architecture to serve a read-only addition was explicitly out of scope
 * and remains so.
 */
export function CaptureWorkLog({ workLog }: { workLog: CaptureWorkLogState }) {
  const session = useSession();
  const { status, events, error } = workLog;

  const lines = buildWorkLogLines({
    events,
    viewerUserId: sessionOwnerUserId(session),
    viewerDisplayName: session.profile?.display_name ?? null,
    // The canonical Crewlink timezone, never the device's (INV-T17). Absent
    // means the time is omitted rather than guessed.
    timeZone: session.profile?.timezone ?? null,
  });

  return (
    <section className="capture-worklog" aria-labelledby="capture-worklog-heading">
      <h2 id="capture-worklog-heading" className="projects-group-heading">
        HISTORY
      </h2>

      {status === 'loading' ? (
        <p className="projects-loading" role="status">
          Loading this Capture’s history…
        </p>
      ) : null}

      {status === 'failed' && error ? (
        <p className="cl-error" role="alert">
          {error}
        </p>
      ) : null}

      {status === 'ready' && lines.length === 0 ? (
        // A Capture always has at least its creation event, so an empty list
        // means something is genuinely absent rather than not yet written —
        // and saying so is better than an empty panel that reads as a bug.
        <p className="projects-empty-body">Nothing has been recorded for this Capture yet.</p>
      ) : null}

      {lines.length > 0 ? (
        <ol className="capture-worklog-list">
          {lines.map((line) => (
            <li key={line.id} className="capture-worklog-entry">
              <p className="capture-worklog-summary">{line.summary}</p>
              <p className="capture-worklog-meta">
                <span className="capture-worklog-actor">{line.actor}</span>
                {line.time ? (
                  <>
                    <span aria-hidden="true"> · </span>
                    <span className="capture-worklog-time">{line.time}</span>
                  </>
                ) : null}
              </p>
            </li>
          ))}
        </ol>
      ) : null}
    </section>
  );
}
